import type { ReactNode } from "react";
import { DatePickerInput, Input, Text, Th, Tr } from "@/components/ui";
import { formatIso, parseIso } from "@/features/datos-generales/utils/iso-date";
import { CAMPOS_FILTRO_FACTURA, placeholderEjemplo, type FacturaFiltroKey, type FiltrosFactura } from "../utils/filtros-factura";

type CampoFactura = (typeof CAMPOS_FILTRO_FACTURA)[number];

/**
 * Recorre las columnas de la tabla y coloca cada campo de factura sobre sus columnas
 * (`colSpan`), dejando celdas vacías donde no hay campo · así la sección queda alineada con
 * los bordes de la fila de filtros de la solicitud.
 */
function celdas(columnas: readonly (string | null)[], render: (c: CampoFactura, span: number) => ReactNode, vacia: (i: number) => ReactNode) {
  const out: ReactNode[] = [];
  for (let i = 0; i < columnas.length; ) {
    const campo = CAMPOS_FILTRO_FACTURA.find((c) => c.columnas[0] === columnas[i]);
    if (campo) {
      out.push(render(campo, campo.columnas.length));
      i += campo.columnas.length;
    } else {
      out.push(vacia(i));
      i += 1;
    }
  }
  return out;
}

/**
 * Sección de filtros por FACTURA de la tabla de «Mis solicitudes», debajo de la fila de
 * filtros de la solicitud: una fila con sus PROPIOS encabezados (UUID, serie-folio, emisor,
 * total, fecha de timbrado) y debajo sus inputs. Cada campo ocupa columnas consecutivas de la
 * tabla (ver `CAMPOS_FILTRO_FACTURA.columnas`) para alinearse con los filtros de arriba. Fondos
 * OPACOS: el encabezado es sticky y las filas no deben verse pasar por detrás al hacer scroll.
 */
export function FilaFiltrosFactura({
  columnas,
  filtros,
  onFiltrar,
}: {
  /** `key` de cada columna de la tabla, en orden (null = columna sin filtro). */
  columnas: readonly (string | null)[];
  filtros: FiltrosFactura;
  onFiltrar: (key: FacturaFiltroKey, valor: string) => void;
}) {
  return (
    <>
      <Tr className="border-0 bg-ink-100 dark:bg-ink-800 [&>th]:shadow-[inset_0_2px_0_var(--color-border)]">
        {celdas(
          columnas,
          (c, span) => (
            <Th key={c.key} colSpan={span} className="!px-2.5 !py-2 align-middle">
              <Text
                size="xs"
                fw={600}
                title={c.label}
                className="min-w-0 truncate whitespace-nowrap uppercase leading-tight text-ink-700 dark:text-ink-200"
              >
                {c.label}
              </Text>
            </Th>
          ),
          (i) => <Th key={`fh-${i}`} className="!px-1 !py-2" />,
        )}
      </Tr>
      <Tr className="border-0 bg-[color-mix(in_srgb,var(--color-foreground)_10%,var(--color-ink-100))] dark:bg-[color-mix(in_srgb,var(--color-foreground)_10%,var(--color-ink-800))] [&>th]:shadow-[inset_0_1px_0_var(--color-border),inset_0_-1px_0_var(--color-border)]">
        {celdas(
          columnas,
          (c, span) => (
            <Th key={c.key} colSpan={span} className="!px-1 !py-1.5 align-top">
              {c.fecha ? (
                <DatePickerInput
                  size="xs"
                  clearable
                  placeholder={placeholderEjemplo(c.placeholder)}
                  value={parseIso(filtros[c.key])}
                  onChange={(d) => onFiltrar(c.key, formatIso(d))}
                  wrapperClassName="normal-case"
                  placeholderClassName="truncate text-[10px] text-muted-foreground/60"
                />
              ) : (
                <Input
                  size="xs"
                  placeholder={placeholderEjemplo(c.placeholder)}
                  value={filtros[c.key] ?? ""}
                  onChange={(e) => onFiltrar(c.key, e.currentTarget.value)}
                  aria-label={`Filtrar por ${c.label} de la factura`}
                  className="normal-case placeholder:text-[10px] placeholder:text-muted-foreground/60"
                />
              )}
            </Th>
          ),
          (i) => <Th key={`ff-${i}`} className="!px-1 !py-1.5" />,
        )}
      </Tr>
    </>
  );
}
